import { useCallback, useState } from "react";
import { isAdmin, type UserInfo } from "@common/auth/session";

const STORAGE_KEY = "salvagers_admin_mode";

export interface AdminMode {
  /** In an admin group, so the toggle is offered. */
  available: boolean;
  /** An admin who has switched admin tools on. */
  on: boolean;
  setOn: (on: boolean) => void;
}

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Admin UI is opt-in, as in waypoint_wars: the admin group only offers the
 * "Admin tools" toggle on the account page, and it starts off. The saved
 * choice never grants on its own, so losing the group drops the tools. This
 * only decides what to show; the API checks the group on every admin route.
 */
export function useAdminMode(user: UserInfo | null): AdminMode {
  const available = isAdmin(user);
  const [stored, setStored] = useState(readStored);

  const setOn = useCallback((on: boolean) => {
    setStored(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? "true" : "false");
    } catch {
      // Storage blocked: the toggle still holds for this page.
    }
  }, []);

  return { available, on: available && stored, setOn };
}
